import Phaser from 'phaser';

// ============================================================================
// WCAG Contrast Utilities
// ============================================================================

/**
 * Convert hex color string to RGB values (0-1 range)
 */
function hexToRgb01(hex: string): [number, number, number] {
    const h = hex.replace('#', '').replace('0x', '');
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    return [r, g, b];
}

/**
 * Convert sRGB channel to linear RGB
 */
function srgbToLinear(c: number): number {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * Calculate relative luminance per WCAG 2.1
 */
function relativeLuminance(hex: string): number {
    const [r, g, b] = hexToRgb01(hex).map(srgbToLinear);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculate WCAG contrast ratio between two colors
 * @returns Contrast ratio (1.0 to 21.0)
 */
export function contrastRatio(fgHex: string, bgHex: string): number {
    const Lfg = relativeLuminance(fgHex);
    const Lbg = relativeLuminance(bgHex);
    const L1 = Math.max(Lfg, Lbg);
    const L2 = Math.min(Lfg, Lbg);
    return (L1 + 0.05) / (L2 + 0.05);
}

/**
 * Check if contrast meets WCAG AA requirements
 * @param fgHex Foreground (text) color
 * @param bgHex Background color
 * @param isLargeText True for text >= 24px regular or >= 18.5px bold
 * @returns True if contrast is sufficient
 */
export function meetsContrastRequirement(fgHex: string, bgHex: string, isLargeText: boolean = false): boolean {
    const ratio = contrastRatio(fgHex, bgHex);
    return isLargeText ? ratio >= 3.0 : ratio >= 4.5;
}

/**
 * Convert number color (0xRRGGBB) to hex string (#RRGGBB)
 */
export function numberToHex(color: number): string {
    return '#' + color.toString(16).padStart(6, '0');
}

// ============================================================================
// Theme types
export interface ThemeColors {
    background: number;
    panelBg: number;
    panelColor: number;
    panelBorderLight: number;
    panelBorderDark: number;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    textAccent: string;
    success: number;
    successHex: string;
    warning: number;
    warningHex: string;
    danger: number;
    dangerHex: string;
    info: number;
    infoHex: string;
    buttonPrimary: number;
    buttonSecondary: number;
    buttonDanger: number;
    buttonNeutral: number;
    weaponColor: number;
    weaponColorHex: string;
    supportColor: number;
    supportColorHex: string;
}

export interface ThemeFonts {
    family: string;
    sizeHuge: number;
    sizeLarge: number;
    sizeMedium: number;
    sizeSmall: number;
    sizeLabel: number;
    minReadable: number;
}

export interface ThemeSpacing {
    panelPadding: number;
    buttonPaddingH: number;
    buttonPaddingV: number;
    buttonMinWidth: number;
    buttonMinHeight: number;
    buttonGap: number;
    elementGap: number;
    sectionGap: number;
}

export interface ThemeBorders {
    bevelWidth: number;
    borderRadius: number;
    borderRadiusSmall: number;
}

export interface Theme {
    version: string;
    name: string;
    colors: ThemeColors;
    fonts: ThemeFonts;
    spacing: ThemeSpacing;
    borders: ThemeBorders;
}

export type ButtonAlign = 'left' | 'center' | 'right' | 'justify';

export interface ButtonConfig {
    text: string;
    color?: number;
    callback: () => void;
    minWidth?: number;
}

// Theme singleton
let theme: Theme | null = null;

export async function loadTheme(): Promise<Theme> {
    if (theme) return theme;

    const response = await fetch('/data/theme.json');
    const data = await response.json();

    // Parse hex color strings to numbers
    const parseColor = (val: string | number): number => {
        if (typeof val === 'string' && val.startsWith('0x')) {
            return parseInt(val, 16);
        }
        return val as number;
    };

    const loadedTheme: Theme = {
        ...data,
        colors: {
            background: parseColor(data.colors.background),
            panelBg: parseColor(data.colors.panelBg),
            panelColor: parseColor(data.colors.panelColor),
            panelBorderLight: parseColor(data.colors.panelBorderLight),
            panelBorderDark: parseColor(data.colors.panelBorderDark),
            textPrimary: data.colors.textPrimary,
            textSecondary: data.colors.textSecondary,
            textMuted: data.colors.textMuted,
            textAccent: data.colors.textAccent,
            success: parseColor(data.colors.success),
            successHex: data.colors.successHex,
            warning: parseColor(data.colors.warning),
            warningHex: data.colors.warningHex,
            danger: parseColor(data.colors.danger),
            dangerHex: data.colors.dangerHex,
            info: parseColor(data.colors.info),
            infoHex: data.colors.infoHex,
            buttonPrimary: parseColor(data.colors.buttonPrimary),
            buttonSecondary: parseColor(data.colors.buttonSecondary),
            buttonDanger: parseColor(data.colors.buttonDanger),
            buttonNeutral: parseColor(data.colors.buttonNeutral),
            weaponColor: parseColor(data.colors.weaponColor),
            weaponColorHex: data.colors.weaponColorHex,
            supportColor: parseColor(data.colors.supportColor),
            supportColorHex: data.colors.supportColorHex,
        },
    };

    // Validate contrast ratios on load
    validateThemeContrast(loadedTheme);

    theme = loadedTheme;
    return loadedTheme;
}

/**
 * Validate all text colors meet WCAG contrast requirements against panel background
 */
function validateThemeContrast(t: Theme): void {
    const bgHex = numberToHex(t.colors.panelBg);
    const textColors = [
        { name: 'textPrimary', color: t.colors.textPrimary },
        { name: 'textSecondary', color: t.colors.textSecondary },
        { name: 'textMuted', color: t.colors.textMuted },
        { name: 'textAccent', color: t.colors.textAccent },
    ];

    for (const { name, color } of textColors) {
        const ratio = contrastRatio(color, bgHex);
        // All our text is >= 24px, so large text threshold (3.0) applies
        // But we enforce 4.5 as our stricter standard
        if (ratio < 4.5) {
            console.warn(
                `[UITheme] Low contrast warning: ${name} (${color}) vs panelBg (${bgHex}) = ${ratio.toFixed(2)} (need >= 4.5)`
            );
        }
    }
}

export function getTheme(): Theme {
    if (!theme) {
        throw new Error('Theme not loaded. Call loadTheme() first.');
    }
    return theme;
}

/**
 * UI helper class for creating themed components with proper flow layout
 */
export class UIBuilder {
    private scene: Phaser.Scene;
    private theme: Theme;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        this.theme = getTheme();
    }

    /**
     * Create a text object with theme styling
     */
    createText(
        x: number,
        y: number,
        content: string,
        size: 'huge' | 'large' | 'medium' | 'small' | 'label' = 'medium',
        color: 'primary' | 'secondary' | 'muted' | 'accent' | string = 'primary',
        bold: boolean = false
    ): Phaser.GameObjects.Text {
        const fontSize = this.getFontSize(size);
        const textColor = this.getTextColor(color);

        const text = this.scene.add.text(x, y, content, {
            fontFamily: this.theme.fonts.family,
            fontSize: `${fontSize}px`,
            color: textColor,
            fontStyle: bold ? 'bold' : 'normal',
        });

        return text;
    }

    /**
     * Create a button that auto-sizes to fit its label
     */
    createButton(
        x: number,
        y: number,
        label: string,
        callback: () => void,
        color: number = this.theme.colors.buttonPrimary,
        fontSize: 'large' | 'medium' | 'small' = 'medium'
    ): Phaser.GameObjects.Container {
        const { spacing, borders, fonts } = this.theme;

        // Create temporary text to measure
        const tempText = this.scene.add.text(0, 0, label, {
            fontFamily: fonts.family,
            fontSize: `${this.getFontSize(fontSize)}px`,
            fontStyle: 'bold',
        });
        const textWidth = tempText.width;
        const textHeight = tempText.height;
        tempText.destroy();

        // Calculate button size with padding, respecting minimums
        const width = Math.max(
            spacing.buttonMinWidth,
            textWidth + spacing.buttonPaddingH * 2
        );
        const height = Math.max(
            spacing.buttonMinHeight,
            textHeight + spacing.buttonPaddingV * 2
        );

        // Create button background
        const bg = this.scene.add.graphics();
        this.drawButtonBg(bg, width, height, color, 1);

        // Create label
        const labelText = this.scene.add.text(0, 0, label, {
            fontFamily: fonts.family,
            fontSize: `${this.getFontSize(fontSize)}px`,
            color: this.theme.colors.textPrimary,
            fontStyle: 'bold',
        });
        labelText.setOrigin(0.5);

        // Create container
        const container = this.scene.add.container(x, y, [bg, labelText]);
        container.setSize(width, height);
        container.setInteractive({ useHandCursor: true });

        // Store dimensions for layout
        container.setData('width', width);
        container.setData('height', height);

        // Hover effects
        container.on('pointerover', () => {
            this.drawButtonBg(bg, width, height, color, 0.7);
        });

        container.on('pointerout', () => {
            this.drawButtonBg(bg, width, height, color, 1);
        });

        container.on('pointerdown', callback);

        return container;
    }

    /**
     * Create a row of buttons with specified alignment
     */
    createButtonRow(
        centerX: number,
        y: number,
        buttons: ButtonConfig[],
        align: ButtonAlign = 'center',
        availableWidth?: number
    ): Phaser.GameObjects.Container[] {
        const { spacing } = this.theme;
        const createdButtons: Phaser.GameObjects.Container[] = [];

        // First pass: create buttons to measure them
        let totalWidth = 0;
        const buttonWidths: number[] = [];

        buttons.forEach((config, i) => {
            const btn = this.createButton(
                0, 0,
                config.text,
                config.callback,
                config.color ?? this.theme.colors.buttonPrimary
            );
            createdButtons.push(btn);

            const width = btn.getData('width') as number;
            buttonWidths.push(width);
            totalWidth += width;

            if (i < buttons.length - 1) {
                totalWidth += spacing.buttonGap;
            }
        });

        // Second pass: position buttons based on alignment
        let currentX: number;

        switch (align) {
            case 'left':
                currentX = centerX - (availableWidth ? availableWidth / 2 : totalWidth / 2);
                break;
            case 'right':
                currentX = centerX + (availableWidth ? availableWidth / 2 : totalWidth / 2) - totalWidth;
                break;
            case 'justify':
                if (availableWidth && buttons.length > 1) {
                    const totalButtonWidth = buttonWidths.reduce((a, b) => a + b, 0);
                    const gap = (availableWidth - totalButtonWidth) / (buttons.length - 1);
                    currentX = centerX - availableWidth / 2 + buttonWidths[0] / 2;

                    createdButtons.forEach((btn, i) => {
                        btn.setPosition(currentX, y);
                        if (i < createdButtons.length - 1) {
                            currentX += buttonWidths[i] / 2 + gap + buttonWidths[i + 1] / 2;
                        }
                    });
                    return createdButtons;
                }
                // If can't justify, fall back to center
                currentX = centerX - totalWidth / 2;
                break;
            case 'center':
            default:
                currentX = centerX - totalWidth / 2;
                break;
        }

        // Position buttons
        createdButtons.forEach((btn, i) => {
            const width = buttonWidths[i];
            btn.setPosition(currentX + width / 2, y);
            currentX += width + spacing.buttonGap;
        });

        return createdButtons;
    }

    /**
     * Draw a beveled panel (CRT monitor style)
     */
    drawBeveledPanel(
        graphics: Phaser.GameObjects.Graphics,
        x: number,
        y: number,
        width: number,
        height: number,
        title?: string
    ): Phaser.GameObjects.Text | null {
        const { colors, borders, fonts } = this.theme;
        const bevel = borders.bevelWidth;

        // Outer dark bevel (bottom-right)
        graphics.fillStyle(colors.panelBorderDark, 1);
        graphics.fillRect(x, y, width, height);

        // Light bevel (top-left)
        graphics.fillStyle(colors.panelBorderLight, 1);
        graphics.fillRect(x, y, width - bevel, height - bevel);

        // Inner dark (creates depth)
        graphics.fillStyle(colors.panelBorderDark, 1);
        graphics.fillRect(x + bevel, y + bevel, width - bevel * 2, height - bevel * 2);

        // Panel background
        graphics.fillStyle(colors.panelBg, 1);
        graphics.fillRect(x + bevel + 2, y + bevel + 2, width - bevel * 2 - 4, height - bevel * 2 - 4);

        // Title bar if provided
        if (title) {
            graphics.fillStyle(colors.panelColor, 1);
            graphics.fillRect(x + bevel + 2, y + bevel + 2, width - bevel * 2 - 4, 36);

            const titleText = this.scene.add.text(x + width / 2, y + bevel + 20, title, {
                fontFamily: fonts.family,
                fontSize: `${fonts.sizeMedium}px`,
                color: colors.textPrimary,
                fontStyle: 'bold',
            });
            titleText.setOrigin(0.5);
            return titleText;
        }

        return null;
    }

    private drawButtonBg(
        graphics: Phaser.GameObjects.Graphics,
        width: number,
        height: number,
        color: number,
        alpha: number
    ): void {
        graphics.clear();
        graphics.fillStyle(color, alpha);
        graphics.fillRoundedRect(-width / 2, -height / 2, width, height, this.theme.borders.borderRadius);
    }

    private getFontSize(size: 'huge' | 'large' | 'medium' | 'small' | 'label'): number {
        const { fonts } = this.theme;
        switch (size) {
            case 'huge': return fonts.sizeHuge;
            case 'large': return fonts.sizeLarge;
            case 'medium': return fonts.sizeMedium;
            case 'small': return fonts.sizeSmall;
            case 'label': return fonts.sizeLabel;
            default: return fonts.sizeMedium;
        }
    }

    private getTextColor(color: 'primary' | 'secondary' | 'muted' | 'accent' | string): string {
        const { colors } = this.theme;
        switch (color) {
            case 'primary': return colors.textPrimary;
            case 'secondary': return colors.textSecondary;
            case 'muted': return colors.textMuted;
            case 'accent': return colors.textAccent;
            default: return color;
        }
    }
}
